import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import ButtonsMainGoTo from "../../../components/Button/ButtonMainGoTo";
import { useTranslation } from "../../../language";
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { API_URL } from "../../../constants";
import { useAuth } from "../../../provider";
import * as c from '../../../constants';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const isAddon = searchParams.get("addon") === "true";

  const { state, authLoading } = useAuth();
  const { t } = useTranslation();

  // --- States ---
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);

  // --- Helper: advance onboarding ---
  const advanceOnboarding = async (stage) => {
    try {
      await axios.patch (
        `${API_URL}/api/onboarding/status/`, 
        { onboarding_status: stage }
      );
    } catch (err) {
      console.error("Failed to advance onboarding:", err);
    }
  };


  // --- Effect: verify payment and advance onboarding ---
  useEffect(() => {

    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // Check if user is authenticated after loading completes
    if (!state.isLoggedIn) {
      setError(t("onb_ps_auth_required"));
      return;
    }

    let isMounted = true; // Prevent setState on unmount

    const verifyPayment = async () => {
      try {
        const targetUrl = isAddon
          ? `${API_URL}/api/stripe/post-success-addon/`
          : `${API_URL}/stripe/post-success/`;

        const res = await axios.post(
          targetUrl, 
          { session_id: sessionId }
        );

        if (!isMounted) return;

        if (res.data?.message) {
          setMessage(t("onb_ps_confirmed"));
        } else {
          setMessage(t("onb_ps_received"));
        }

        setVerified(true);

        // Advance onboarding (e.g., to stage 5: Payment complete)
        await advanceOnboarding(2);

        // Give user 2.5s to see success, then go to next onboarding step
        setTimeout(() => {
          if (isMounted) 
            console.log("Navigating now...");
            navigate("/LabAppointment", { replace: true });
        }, 2500);
      } catch (err) {
        if (!isMounted) return;

        console.error("Payment verification failed:", err);
        setError(
          err.response?.data?.error ||
            t("onb_ps_verify_failed")
        );
      }
    };

    if (sessionId) verifyPayment();
    else setError(t("onb_ps_missing_session"));

    return () => {
      isMounted = false;
    };
  }, [sessionId, isAddon, navigate, state.isLoggedIn, state.token]); 

  // fetch stripe subscriptions
  useEffect(() => {
    axios.get(`${c.API_URL}/api/profile/stripecustomer`)
      .then((res) => {
        setSubscription(res.data);
      })
      .catch((err) => {
        console.error("Error fetching subscription:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);



  // --- UI ---
  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          borderRadius: 3,
          boxShadow: 3,
          backgroundColor: "background.paper",
        }}
      >
        {error ? (
          <>
            <Typography variant="h5" color="error" gutterBottom>
              ⚠️ {t("onb_ps_error_title")}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              {error}
            </Typography>
            <Button
              component={RouterLink}
              to="/"
              variant="contained"
              color="primary"
            >
              {t("onb_ps_try_again")}
            </Button>
          </>
        ) : verified ? (
          <>
            <Typography variant="h4" gutterBottom>
              {t("onb_ps_success_title")}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              {message}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <ButtonsMainGoTo name={t("onb_ps_continue")} to="/LapAppointment" />
            </Stack>
          </>
        ) : (
          <>
            <Typography variant="h5" gutterBottom>
              {t("onb_ps_processing")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {message ?? t("onb_ps_verifying")}
            </Typography>
            <CircularProgress color="primary" />
          </>
        )}
      </Box>
    </Container>
  );
}


/*
1 authLoading starts as true
2 AuthProvider restores token from localStorage
3 Sets authLoading = false when done
4 PaymentSuccess waits until authLoading === false before making API calls
5 Navigation to /LabAppointment happens with auth fully restored
6 Route guards see authenticated user → no login redirect

This ensures the authentication is fully hydrated before any navigation or API calls occur.

*/